import React from "react"

import { DialectCard } from "./dialect_info"

export interface ConjugationForm {
  person: string
  label: string
  form: string
  /** Dialect-specific forms where they differ from standard, e.g. { munster: "rithid" }. */
  byDialect?: Record<string, string>
}

export interface ConjugationTense {
  tense: string
  label: string
  forms: ConjugationForm[]
}

export interface ConjugationChart {
  tenses: ConjugationTense[]
  irregular?: boolean
  /** Non-standard dialects that have variant forms. */
  dialects?: string[]
  /** Attribution for the generated forms, shown under the chart, e.g. "BuNaMo". */
  source?: string
}

type Person = { person: string; label: string }
type Resolve = (form?: ConjugationForm) => {
  value?: string
  highlight: boolean
}

// A persons x tenses grid. `resolve` picks the value (and whether to highlight) for each cell, so the
// same grid renders the standard forms and a dialect's variations.
const Grid: React.FC<{
  persons: Person[]
  tenses: ConjugationTense[]
  resolve: Resolve
}> = ({ persons, tenses, resolve }) => (
  <div className="mt-3 overflow-x-auto">
    <table className="border border-secondary-200 text-sm w-full table-fixed">
      <thead>
        <tr className="bg-secondary-50">
          <th className="border border-secondary-200 px-3 py-1 text-left font-semibold text-muted-foreground">
            Subject
          </th>
          {tenses.map((tense) => (
            <th
              key={tense.tense}
              className="border border-secondary-200 px-3 py-1 text-left font-semibold"
            >
              {tense.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {persons.map((person) => (
          <tr key={person.person}>
            <th className="border border-secondary-200 px-3 py-1 text-left font-normal text-muted-foreground">
              {person.label}
            </th>
            {tenses.map((tense) => {
              const cell = resolve(
                tense.forms.find((form) => form.person === person.person)
              )
              return (
                <td
                  key={tense.tense}
                  className={`border border-secondary-200 px-3 py-1 ${
                    cell.highlight ? "font-semibold text-secondary-700" : ""
                  }`}
                >
                  {cell.value}
                </td>
              )
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

/**
 * Renders a verb's generated conjugation. The standard (Caighdeán) grid shows on top; each dialect
 * with variant forms is stacked below with its notice and just the rows that vary. Forms come from
 * the record's metadata via IrishGrammar::Verb (regular by rule, irregular from a table).
 */
const ConjugationTable: React.FC<{ conjugation?: ConjugationChart | null }> = ({
  conjugation,
}) => {
  const [open, setOpen] = React.useState(true)
  const tenses = conjugation?.tenses ?? []
  if (tenses.length === 0) return null

  // Persons are consistent across tenses; take the ordering from the first tense.
  const persons: Person[] = tenses[0].forms.map((form) => ({
    person: form.person,
    label: form.label,
  }))

  const dialects = conjugation?.dialects ?? []

  const variesFor = (dialectKey: string) => (person: Person) =>
    tenses.some(
      (tense) =>
        tense.forms.find((form) => form.person === person.person)?.byDialect?.[
          dialectKey
        ]
    )

  return (
    <div className="mt-6" data-testid="conjugation-table" id="verb-conjugation">
      <p className="text-xs uppercase text-muted-foreground flex items-center gap-2">
        Conjugation
        {conjugation?.irregular && (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] normal-case text-amber-800">
            irregular verb
          </span>
        )}
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="ml-auto normal-case underline decoration-dotted"
          aria-expanded={open}
        >
          {open ? "Hide" : "Show"}
        </button>
      </p>

      {open && (
        <>
          {/* Official notice on top, then the standard grid. */}
          {dialects.length > 0 && <DialectCard dialect="standard" />}
          <Grid
            persons={persons}
            tenses={tenses}
            resolve={(form) => ({ value: form?.form, highlight: false })}
          />

          {/* Each dialect: its notice, then only the rows that vary from standard. */}
          {dialects.map((dialectKey) => {
            const varyingPersons = persons.filter(variesFor(dialectKey))
            if (varyingPersons.length === 0) return null

            return (
              <div key={dialectKey} className="mt-4">
                <DialectCard dialect={dialectKey} />
                <Grid
                  persons={varyingPersons}
                  tenses={tenses}
                  resolve={(form) => {
                    const variant = form?.byDialect?.[dialectKey]
                    return {
                      value: variant ?? form?.form,
                      highlight: Boolean(variant),
                    }
                  }}
                />
              </div>
            )
          })}

          {/* Honest note: these are generated, with hand-corrections layered on where the rules fall short. */}
          <p className="mt-3 text-xs text-muted-foreground">
            Irish verbs mostly follow a first- or second-conjugation pattern, with a handful of irregular
            verbs. These forms are generated from the verb&rsquo;s pattern and hand-corrected where needed, so
            some may not be perfect. Spot a mistake? Let us know.
          </p>
          {conjugation?.source && (
            <p className="mt-1 text-xs text-muted-foreground">
              source: {conjugation.source}
            </p>
          )}
        </>
      )}
    </div>
  )
}

export default ConjugationTable
